import Field from "@/components/Field";
import ProvinceSelect from "@/components/ProvinceSelect";
import { Input } from "@/components/ui/input";
import { useTranslation } from "react-i18next";
import { DebounceInput } from "./DebounceInput";
import { isRequired } from "@/lib/form";
import { ZodObject, ZodRawShape } from "zod";
import {
  ChangeEventHandler,
  createContext,
  PropsWithChildren,
  useContext,
} from "react";
import Fieldset from "@/components/Fieldset";
import { address } from "@/schemas/address";
import { cn } from "@/lib/utils";
import {
  ControllerRenderProps,
  FieldValues,
  useFormContext,
} from "react-hook-form";

interface AddressFieldsetProps<P extends ZodObject<ZodRawShape>> {
  schema?: P;
  postfix?: string;
  className?: string;
  // Callback being called for any input field change
  onChange?: () => void;
}

type OnChangeHandler = (
  field: ControllerRenderProps<FieldValues, string>,
) => ChangeEventHandler<HTMLInputElement>;

interface AddressFieldsetContextValue {
  schema: ZodObject<ZodRawShape>;
  postfix: string;
  onChange: OnChangeHandler;
}

export default function AddressFieldset<P extends ZodObject<ZodRawShape>>({
  children,
  schema,
  className,
  postfix,
  onChange,
}: PropsWithChildren<AddressFieldsetProps<P>>) {
  const onChangeHandler: OnChangeHandler = (field) => (e) => {
    field.onChange(e);
    onChange?.();
  };
  return (
    <AddressFieldsetProvider
      value={{
        schema: schema || address,
        postfix: postfix || "",
        onChange: onChangeHandler,
      }}
    >
      <Fieldset className={cn("@container flex flex-row flex-wrap", className)}>
        {children}
      </Fieldset>
    </AddressFieldsetProvider>
  );
}

const AddressFieldsetContext = createContext<AddressFieldsetContextValue>(
  {} as AddressFieldsetContextValue,
);

const AddressFieldsetProvider = AddressFieldsetContext.Provider;

const useAddressFieldset = (): AddressFieldsetContextValue => {
  const context = useContext(AddressFieldsetContext);
  return context;
};

interface FieldProps {
  name?: string;
  className?: string;
}

const Province = ({ name = "province_id", className }: FieldProps) => {
  const { t } = useTranslation();
  const { schema, postfix } = useAddressFieldset();
  const nameWithPostfix = name + postfix;
  const form = useFormContext();

  return (
    <div className="grow w-full">
      <Field
        name={nameWithPostfix}
        required={isRequired(schema.shape[nameWithPostfix])}
        label={t("addressFieldset.province")}
        render={({ field }) => (
          <ProvinceSelect
            {...field}
            className={className}
            defaultItem={{ id: field.value, name: form.getValues("province") }}
          />
        )}
      />
    </div>
  );
};

const Longitude = ({ name = "longitude", className }: FieldProps) => {
  const { t } = useTranslation();
  const { schema, postfix, onChange } = useAddressFieldset();
  const nameWithPostfix = name + postfix;

  return (
    <div className="grow">
      <Field
        name={nameWithPostfix}
        required={isRequired(schema.shape[nameWithPostfix])}
        label={t("addressFieldset.longitude")}
        render={({ field }) => (
          <DebounceInput
            {...field}
            className={className}
            type="number"
            onChange={onChange(field)}
          />
        )}
      />
    </div>
  );
};

const Latitude = ({ name = "latitude", className }: FieldProps) => {
  const { t } = useTranslation();
  const { schema, postfix, onChange } = useAddressFieldset();
  const nameWithPostfix = name + postfix;

  return (
    <div className="grow">
      <Field
        name={nameWithPostfix}
        required={isRequired(schema.shape[nameWithPostfix])}
        label={t("addressFieldset.latitude")}
        render={({ field }) => (
          <DebounceInput
            {...field}
            className={className}
            type="number"
            onChange={onChange(field)}
          />
        )}
      />
    </div>
  );
};

const City = ({ name = "city", className }: FieldProps) => {
  const { t } = useTranslation();
  const { schema, postfix, onChange } = useAddressFieldset();
  const nameWithPostfix = name + postfix;

  return (
    <div className="grow">
      <Field
        name={nameWithPostfix}
        required={isRequired(schema.shape[nameWithPostfix])}
        label={t("addressFieldset.city")}
        render={({ field }) => (
          <Input
            {...field}
            value={field.value ?? ""}
            className={className}
            onChange={onChange(field)}
          />
        )}
      />
    </div>
  );
};

const PostalCode = ({ name = "postal_code", className }: FieldProps) => {
  const { t } = useTranslation();
  const { schema, postfix, onChange } = useAddressFieldset();
  const nameWithPostfix = name + postfix;
  return (
    <div className="@sm:w-1/4 @sm:grow-0 grow">
      <Field
        name={nameWithPostfix}
        required={isRequired(schema.shape[nameWithPostfix])}
        label={t("addressFieldset.postal_code")}
        render={({ field }) => (
          <Input
            {...field}
            value={field.value ?? ""}
            className={className}
            onChange={onChange(field)}
          />
        )}
      />
    </div>
  );
};

const StreetName = ({ name = "street_name", className }: FieldProps) => {
  const { t } = useTranslation();
  const { schema, postfix, onChange } = useAddressFieldset();
  const nameWithPostfix = name + postfix;

  return (
    <div className="@sm:w-full grow">
      <Field
        name={nameWithPostfix}
        required={isRequired(schema.shape[nameWithPostfix])}
        label={t("addressFieldset.street_name")}
        render={({ field }) => (
          <Input
            {...field}
            value={field.value ?? ""}
            className={className}
            onChange={onChange(field)}
          />
        )}
      />
    </div>
  );
};

const StreetNumber = ({ name = "street_number", className }: FieldProps) => {
  const { t } = useTranslation();
  const { schema, postfix, onChange } = useAddressFieldset();
  const nameWithPostfix = name + postfix;

  return (
    <div className="grow">
      <Field
        name={nameWithPostfix}
        required={isRequired(schema.shape[nameWithPostfix])}
        label={t("addressFieldset.street_number")}
        render={({ field }) => (
          <Input
            {...field}
            value={field.value ?? ""}
            className={className}
            onChange={onChange(field)}
          />
        )}
      />
    </div>
  );
};
const ApartmentNumber = ({
  name = "apartment_number",
  className,
}: FieldProps) => {
  const { t } = useTranslation();
  const { schema, postfix, onChange } = useAddressFieldset();
  const nameWithPostfix = name + postfix;

  return (
    <div className="grow">
      <Field
        name={nameWithPostfix}
        required={isRequired(schema.shape[nameWithPostfix])}
        label={t("addressFieldset.apartment_number")}
        render={({ field }) => (
          <Input
            {...field}
            value={field.value ?? ""}
            className={className}
            onChange={onChange(field)}
          />
        )}
      />
    </div>
  );
};

const ContactPerson = ({ name = "contact_person", className }: FieldProps) => {
  const { t } = useTranslation();
  const { schema, postfix, onChange } = useAddressFieldset();
  const nameWithPostfix = name + postfix;

  return (
    <div className="grow">
      <Field
        name={nameWithPostfix}
        required={isRequired(schema.shape[nameWithPostfix])}
        label={t("addressFieldset.contact_person")}
        render={({ field }) => (
          <Input
            {...field}
            value={field.value ?? ""}
            className={className}
            onChange={onChange(field)}
          />
        )}
      />
    </div>
  );
};

const Email = ({ name = "email", className }: FieldProps) => {
  const { t } = useTranslation();
  const { schema, postfix, onChange } = useAddressFieldset();
  const nameWithPostfix = name + postfix;

  return (
    <div className="grow">
      <Field
        name={nameWithPostfix}
        required={isRequired(schema.shape[nameWithPostfix])}
        label={t("addressFieldset.email")}
        render={({ field }) => (
          <Input
            {...field}
            value={field.value ?? ""}
            className={className}
            onChange={onChange(field)}
          />
        )}
      />
    </div>
  );
};

const PhoneNumber = ({ name = "phone_number", className }: FieldProps) => {
  const { t } = useTranslation();
  const { schema, postfix, onChange } = useAddressFieldset();
  const nameWithPostfix = name + postfix;

  return (
    <div className="grow">
      <Field
        name={nameWithPostfix}
        required={isRequired(schema.shape[nameWithPostfix])}
        label={t("addressFieldset.phone_number")}
        render={({ field }) => (
          <Input
            {...field}
            value={field.value ?? ""}
            className={className}
            onChange={onChange(field)}
          />
        )}
      />
    </div>
  );
};

AddressFieldset.ContactPerson = ContactPerson;
AddressFieldset.Email = Email;
AddressFieldset.PhoneNumber = PhoneNumber;
AddressFieldset.Province = Province;
AddressFieldset.Latitude = Latitude;
AddressFieldset.Longitude = Longitude;
AddressFieldset.City = City;
AddressFieldset.PostalCode = PostalCode;
AddressFieldset.StreetName = StreetName;
AddressFieldset.StreetNumber = StreetNumber;
AddressFieldset.ApartmentNumber = ApartmentNumber;
